import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Loader, EmptyState, Money } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShoppingCart, Trash2, Store } from "lucide-react";

export default function Cart() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/cart").then((r) => setData(r.data)).catch(() => setData({ groups: [] }));
  useEffect(() => { load(); }, []);

  async function remove(pid) {
    await api.post("/cart/remove", { product_id: pid, quantity: 0 });
    toast.success("Removed"); load();
  }

  async function placeOrder() {
    setBusy(true);
    try {
      const { data: res } = await api.post("/orders/create-from-cart");
      nav("/retailer/checkout", { state: { orders: res.orders } });
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  }

  if (!data) return <Loader />;
  if (!data.groups?.length) return <EmptyState title="Your cart is empty" hint="Add products from the marketplace to get started." icon={ShoppingCart}
    action={<Button className="bg-forest text-white hover:bg-forest/90" onClick={() => nav("/retailer/marketplace")}>Browse marketplace</Button>} />;

  const grand = data.groups.reduce((s, g) => s + g.total, 0);

  return (
    <div className="space-y-5">
      <h1 className="font-heading text-2xl font-semibold text-forest">Your Cart</h1>
      {data.groups.length > 1 && (
        <p className="rounded-md bg-psage/40 px-4 py-2 text-sm text-forest">Items are grouped by manufacturer. Each manufacturer becomes a separate order.</p>
      )}
      {data.groups.map((g) => (
        <div key={g.manufacturer_id} data-testid={`cart-group-${g.manufacturer_id}`} className="rounded-xl border border-border bg-card p-5">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium text-forest"><Store className="h-4 w-4" /> {g.manufacturer_name}</div>
          <div className="space-y-3">
            {g.items.map((i) => (
              <div key={i.product_id} className="flex items-center gap-3 border-b border-border/60 pb-3 last:border-0 last:pb-0">
                <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md bg-psage/40">
                  {i.image ? <img src={i.image} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-msage">{i.brand?.[0]}</div>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">{i.name}</p>
                  <p className="num text-xs text-ink/50">{i.quantity} × {"₹" + i.retailer_price}</p>
                </div>
                <Money value={i.line_value} className="text-forest" />
                <Button data-testid={`cart-remove-${i.product_id}`} variant="ghost" size="icon" className="text-red-500 hover:bg-red-50" onClick={() => remove(i.product_id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-1 border-t border-border pt-3 text-sm">
            <Row label="Product value" value={g.product_value} />
            <Row label="GST" value={g.gst} />
            <Row label="Shipping" value={g.shipping} />
            <Row label="Order total" value={g.total} bold />
          </div>
        </div>
      ))}
      <div className="sticky bottom-20 flex items-center justify-between rounded-xl border border-border bg-forest p-4 text-white md:bottom-4">
        <div><span className="text-sm text-psage">Grand total</span><div className="num text-2xl font-semibold">{"₹" + grand.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div></div>
        <Button data-testid="cart-checkout-btn" className="bg-white text-forest hover:bg-psage" disabled={busy} onClick={placeOrder}>Proceed to Checkout</Button>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "font-semibold text-forest" : "text-ink/70"}`}>
      <span>{label}</span><Money value={value} className={bold ? "text-forest" : "text-ink"} />
    </div>
  );
}
